import React, { useEffect, useMemo, useState } from 'react'
import { SetLoading } from '@arema/components/Classes'
import { CorteOrdersConcilia } from '../../AdminClasses';
import { Button, Header, Icon, Input, Message, Table, Modal, useFrontier } from 'react-frontier2'
import { addCommas, bindClick, fromUnix } from '@arema/components/Util'
import { Link } from 'react-router-dom';
import API from '../../API'
import Validator from '@arema/components/Validator'

export interface ConciliaOrdersModalProps {
	corte_id: number,
	open: boolean,
	onClose: ()=>void,
	onConcilia: (corte_id: number, amount: number)=>void
}

interface SelectedPayment{
	payment_id: number,
	amount: string,
}

function ConciliaOrdersModal(props: ConciliaOrdersModalProps){
	var { Toast } = useFrontier();
	var [orders, setOrders] = useState<CorteOrdersConcilia[]>(null);
	var [deposits, setDeposits] = useState<{
		deposit_id: number,
		date_deposit: number,
		source_id: number,
		source_name: string,
		deposit_type: number,
		deposit_type_name: number,
		description: string,
		amount: number,
	}[]>(null);
	var [loadError, setLoadError] = useState<string>(null);
	var [prompts, setPrompts] = useState<string[]>(null);
	var [selectedPayments, setSelectedPayments] = useState<SelectedPayment[]>([]);
	var [dateDeposit, setDateDeposit] = useState<Date>(null);

	useEffect(()=>{
		setPrompts(null);
		if(props.open){
			loadOrders();
		}else{
			setDeposits([]);
			setOrders(null);
			setDateDeposit(null);
			setSelectedPayments([]);
		}
	}, [props.corte_id, props.open])

	var {
		total_selected,
		total_conciliado,
		total_conciliado_real
	} = useMemo(()=>{
		var concilia: number = 0, concilia_real: number = 0;
		for(var i of selectedPayments){
			var ord = orders.find(a=>a.payment_id===i.payment_id);
			var amount = i.amount && i.amount.length>0 ? parseFloat(i.amount) : 0;
			if(Number.isNaN(amount)) amount = 0;

			if(!ord){
				concilia += amount;
			}else{
				concilia += Math.min(Math.max(ord.total-ord.total_deposit, 0), amount);
			}
			concilia_real += amount;
		}

		var already = orders ? orders.reduce((a,b)=>a+b.total_deposit, 0) : 0;
		return {
			total_selected: concilia_real,
			total_conciliado: concilia+already,
			total_conciliado_real: concilia_real+already,
		};
	}, [selectedPayments, orders]);

	var total_corte = useMemo(()=>{
		if(!orders) return 0;
		return orders.reduce((a,b)=>a+b.total, 0);
	}, [orders]);

	var total_deposits = useMemo(()=>{
		if(!deposits) return 0;
		return deposits.reduce((a,b)=>a+b.amount, 0);
	}, [deposits]);
	
	var total_balance = Math.max(total_deposits-total_conciliado, 0);

	var loadOrders = ()=>{
		API.getPdvOrderConcilia(props.corte_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setOrders(res.data.orders);
			setDeposits(res.data.deposits || 0);
		}).catch(err=>{
			setLoadError('Error inesperado cargando las ordenes (LCL-1)');
		})
	}

	var onSelectOrder = (order: CorteOrdersConcilia)=>{
		if(selectedPayments.findIndex(ord=>ord.payment_id === order.payment_id)!=-1){
			setSelectedPayments(ords=>{
				return ords.filter(o=>o.payment_id !== order.payment_id)
			});
		}else{
			var remaining = order.total-order.total_deposit;
			if(remaining<=0) return;
			var amount = Math.min(total_balance, remaining);
			if(amount<=0) return;
			setSelectedPayments(p=>([
				...p,
				{
					payment_id: order.payment_id,
					amount: amount.toString()
				}
			]));
		}
	}

	var onPaymentAmount = (payment_id: number)=>{
		return (v: string)=>{
			var amount = parseFloat(v);
			if(Number.isNaN(amount)) amount = 0;
			var py = [...selectedPayments];
			var pix = py.findIndex(a=>a.payment_id===payment_id);
			if(pix!=-1){
				py[pix].amount = v
			}
			setSelectedPayments(py);
		}
	}

	var onSave = (setLoading: SetLoading)=>{
		var { valid, prompts } = Validator({ orders: selectedPayments, date_deposit: dateDeposit }, {
			orders: [
				{ rule: 'minLength', params: [1], prompt: 'Favor de seleccionar por lo menos 1 pago' },
				{ rule: (v: SelectedPayment[])=>{
					for(let i of v){
						var amt = parseFloat(i.amount);
						if(Number.isNaN(i.amount)) return false;
						var ord = orders.find(a=>a.payment_id===i.payment_id);
						if(Math.max(ord.total-ord.total_deposit, 0)<amt){
							return false;
						}
					}
					return true;
				}, prompt: 'Una o mas cantidades ingresadas no son válidas' }
			],
			date_deposit: [{
				rule: 'empty', prompt: 'Favor de seleccionar la fecha de deposito'
			}]
		});
		setPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.saveDepositPayment(Math.floor(dateDeposit.getTime()/1000), selectedPayments.map(a=>({
			payment_id: a.payment_id,
			amount: parseFloat(a.amount)
		}))).then(res=>{
			if(res.error) return setPrompts([res.message]);
			Toast.success('Se han conciliado los pagos seleccionados del corte');
			props.onConcilia(props.corte_id, total_conciliado_real);
			props.onClose();
		}).catch(err=>{
			setPrompts(['Error inesperado conciliando las ordenes seleccionadas (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var autoSelect = ()=>{
		var already_deposit = orders ? orders.reduce((a,b)=>a+b.total_deposit, 0) : 0;
		var total_deposits = deposits.reduce((a,b)=>a+b.amount, 0);

		var balance = total_deposits-already_deposit;

		var payments : SelectedPayment[] = [];
		for(var i of [...orders].sort((b,a)=>Math.max(a.total-a.total_deposit, 0)-Math.max(b.total-b.total_deposit, 0))){
			var remaining = Math.max(i.total-i.total_deposit, 0);
			var max_available = Math.min(balance, remaining);
			if(max_available>0){
				balance -= max_available;
				payments.push({
					payment_id: i.payment_id,
					amount: max_available.toString(),
				});
			}
		}
		setSelectedPayments(payments);
	}

	return <Modal open={props.open} onClose={props.onClose}>
		<Modal.Header>Conciliar corte {props.corte_id}</Modal.Header>
		<Modal.Content style={{ paddingBottom: 20 }}>
			{(!orders || !deposits) ? (
				<Header text='Cargando ordenes' loading />
			) : (
				loadError ? (
					<Header text='Error' subtext={loadError} />
				) : dateDeposit===null ? <>
					<Message header='Selecciona el deposito' centered>
						Se requiere seleccionar el deposito que se usará para conciliar las ordenes, ya que se necesita saber la fecha exacta del deposito para contabilidad
					</Message>
					<Table selectable style={{ marginTop: 15 }}>
						<Table.Head
							title='Deposito'
							data={[['ID', 'Tipo', 'Información', 'Fecha', 'Cantidad']]}
							collapsingIndexes={[0, 1, 3, 4]}
						/>
						<Table.Body>
							{deposits.map(a=>(
								<Table.Row
									collapsingIndexes={[0, 1, 3, 4]}
									onClick={bindClick(setDateDeposit, fromUnix(a.date_deposit).toJSDate())}
									data={[
										a.deposit_id,
										a.source_name || a.deposit_type_name,
										a.description,
										fromUnix(a.date_deposit).toFormat('dd/MMM/yyyy'),
										addCommas(a.amount)
									]}
								/>
							))}
							{deposits.length==0 && (
								<Table.Cell empty row colSpan={100} selectable={false}>
									No hay depositos para este corte
								</Table.Cell>
							)}
						</Table.Body>
					</Table>
				</> : (<>
					<Table style={{ margin: 'auto', marginBottom: 20 }}>
						<Table.Head
							title='Ordenes'
							data={[['ID', 'Orden', 'Pago', 'Conciliado', 'Restante', 'Pendiente']]}
							collapsingIndexes={[0, 1, 2, 4]}
							titleRight={(
								<Button size='tiny' color='black' text='Agregar disponibles' iconName='plus-circle' onClick={autoSelect} />
							)}
						/>
						<Table.Body>
							{orders.length===0 && (
								<Table.Cell row empty colSpan={100} value={'No hay ordenes'} />
							)}
							{orders.map(ord=>{
								var payment = selectedPayments?.find(s=>s.payment_id === ord.payment_id);
								var grayed = total_balance<0 && !payment;
								var remaining = Math.max(ord.total-ord.total_deposit, 0);
								var payment_amount = payment ? parseFloat(payment.amount) : 0;
								if(Number.isNaN(payment_amount)) payment_amount = 0;
								var max_available = Math.min(ord.total-ord.total_deposit, total_balance);

								return <Table.Row 
									key={`CRTORC-${ord.payment_id}`} 
									collapsingIndexes={[0, 1, 2, 4, 5, 6]} 
									style={{ opacity: grayed ? 0.5 : 1, color: 'black' }}
								>
									<Table.Cell collapsing>{ord.payment_id}</Table.Cell>
									<Table.Cell collapsing>
										<Link to={`/orders/@${ord.order_id}`} className='normal' target='_blank'>
											{ord.order_id} 
										</Link>
									</Table.Cell>
									<Table.Cell collapsing>{`$${addCommas(ord.total)}`}</Table.Cell>
									<Table.Cell collapsing>{`$${addCommas(ord.total_deposit)}`}</Table.Cell>
									<Table.Cell collapsing>{`${addCommas(remaining)}`}</Table.Cell>
									<Table.Cell>
										<Input
											readonly={!payment}
											type={payment && payment_amount>remaining ? 'error' : (!payment && max_available<remaining ? 'warning' : (payment && payment_amount<remaining) ? 'warning' : 'normal')}
											style={{ marginBottom: 0 }}
											value={!payment ? addCommas(max_available) : payment.amount}
											inputStyle={!!payment ? null : { color: 'gray' }}
											onChange={payment ? onPaymentAmount(payment.payment_id) : null}
											buttons={(
												<Button icon iconName={payment ? 'check' : 'plus'} color={payment ? 'black' : null} onClick={bindClick(onSelectOrder, ord)} disabled={(total_balance<0 && !payment) || remaining<=0} />
											)} />
									</Table.Cell>
								</Table.Row>
							})}
							<Table.Row header>
								<Table.Cell colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total</Table.Cell>
								<Table.Cell style={{ fontSize: 16, color: total_conciliado_real>total_deposits ? 'brown' : 'black' }}>
									{total_conciliado_real>total_deposits && (
										<Icon name='exclamation-triangle' style={{ marginRight: 5 }} />
									)}
									${addCommas(total_selected)}
								</Table.Cell>
							</Table.Row>
						</Table.Body>
					</Table>
					<Table celled style={{ marginBottom: 15, fontSize: 20 }}>
						<Table.Head
							title='Resumen'
							data={[['Total corte', 'Depositado', 'Conciliado', 'Restante deposito']]}
							centeredIndexes={[0, 1, 2, 3]}
							collapsingIndexes={[0, 1, 2, 3]}
							style={{ fontSize: 11 }}
						/>
						<Table.Body style={{ textAlign: 'center' }} data={[[
							addCommas(total_corte), 
							addCommas(total_deposits), 
							<div style={{ color: total_conciliado_real>total_deposits ? 'brown' : 'black' }}>	
								{total_conciliado_real>total_deposits && (
									<Icon name='triangle-exclamation' />
								)}
								{addCommas(total_conciliado)}
							</div>,
							<b style={{ color: total_conciliado_real>total_deposits ? 'brown' : 'black' }}>
								{total_conciliado_real>total_deposits && (
									<Icon name='triangle-exclamation' />
								)}
								{addCommas(total_deposits-total_conciliado_real)}
							</b>
						]]} />
					</Table>
					<Input label="Fecha de deposito" calendar={{
						date: dateDeposit || null, 
						mode: 'date',
						onSelected: setDateDeposit
					}} />
					<Message type='error' list={prompts} style={{ marginTop: 15 }} />
				</>)
			)}
		</Modal.Content>
		<Modal.Actions>
			<Button text='Cerrar' basic onClick={props.onClose} />
			{!!dateDeposit && (
				<Button text='Conciliar' color='black' onClick={onSave} />
			)}
		</Modal.Actions>
	</Modal>
}

export default ConciliaOrdersModal